import { Injectable, Output, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { AuthService } from './auth.service';
import { environment as env } from '../../environments/environment';
import { ToastService } from './toast.service';
import { llog } from '../@shared/helpers';

@Injectable({
  providedIn: 'root'
})
export class DataAgreementsService {

  constructor(private http: HttpClient,
    private loadingService: LoadingService,
    private authService: AuthService,
    private toast: ToastService) {
  }

  private _get_headers() {
    return {      withCredentials: true
    };
  }

  // Resolve a customer slug + display name from a hostname (e.g.
  // 'scanners.theotrade.com' → { slug: 'theo-trade', name: 'TheoTrade' }).
  // Backed by GET /rest/brand-config?host=<host>, which checks
  // Customers.domains[] and any registered Scanners Url hosts.
  // Used as a fallback when ?customer= isn't on the URL so the page can
  // still identify which customer's agreements to show when reached via
  // a customer-owned domain (e.g. proxied through Vercel rewrites).
  async getBrandByHost(host: string): Promise<{ slug?: string; name?: string; tagline?: string } | null> {
    try {
      const res: any = await this.http.get(
        `${env.apiUrl}/rest/brand-config?host=${encodeURIComponent(host)}`,
        this._get_headers(),
      ).toPromise();
      if (!res || res.err) return null;
      return res;
    } catch (e: any) {
      llog('Error getBrandByHost() =>', e);
      return null;
    }
  }

  async getUserAgreements(cid: string = ''): Promise<any> {
    let _id = this.loadingService.add('Loading exchange agreements');
    try {
      let res: any = await this.http.get(
        `${env.apiUrl}/rest/user/data-agreements?customer=${cid}`,
        this._get_headers(),
      ).toPromise();

      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      llog('Error getUserAgreements() =>', e);
      this.toast.error('Failed to get exchange agreements', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async updateUserAgreements(data: any): Promise<any> {
    let _id = this.loadingService.add('Updating exchange agreements');

    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements`,
        data,
        this._get_headers(),
      ).toPromise();

      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      llog('Error updateUserAgreements() =>', e);
      this.toast.error('Failed to update exchange agreements', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async downloadAgreements(customer: any): Promise<any> {
    let _id = this.loadingService.add('Downloading exchange agreements');
    try {
      let res: any = await this.http.get(
        `${env.apiUrl}/rest/user/data-agreements/download?customer=${customer}`,
        {
          responseType: 'blob',          withCredentials: true,
        },
      ).toPromise();

      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      llog('Error downloadAgreements() =>', e);
      this.toast.error('Failed to download exchange agreements. Please try again.');
      this.loadingService.remove(_id);
      return null;
    }
  }

  async sendVerificationCode(email: any) {
    let _id = this.loadingService.add('Sending code');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/send-code`,
        {email: email},
        this._get_headers(),
      ).toPromise();

      if (res?.err) throw new Error(res.err);
      this.toast.success('Verification code sent.');
      this.loadingService.remove(_id);
      return res;
    }
    catch(e: any) {
      llog('Error sendVerificationCode() =>', e);
      this.toast.error('Failed to send verification code.', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async validateVerificationCode(code: any) {
    let _id = this.loadingService.add('Verifying code');
    try {
      let res: any = await this.http.post(
        `${env.apiUrl}/rest/user/data-agreements/validate-code`,
        {code: code},
        this._get_headers(),
      ).toPromise();

      if (res?.err) throw new Error(res.err);
      this.toast.success('Your email has been verified.');
      this.loadingService.remove(_id);
      return res;
    }
    catch(e: any) {
      llog('Error validateVerificationCode() =>', e);
      this.toast.error('Failed to verify email address.', e.message);
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }
}